// src/components/MeetRecaps.js
import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { FaSearch, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import instaEmbeds from "../data/instaEmbeds.json";
import recapsHeader from "../assets/images/recaps-header.jpg";
import "./MeetRecaps.css";

const EMBEDS_PER_PAGE = 4;

// Safety: strip any <script> that might live in legacy JSON before we inject the
// blockquote. Instagram's embed.js (loaded once below) does the real rendering.
const stripScripts = (html) => html.replace(/<script[\s\S]*?<\/script>/gi, "");

// A stable, content-derived key so React reconciles each post by identity (not
// array position) as pages/search change, giving embed.js fresh blockquotes.
const keyFor = (embed, index) => {
  const match = embed.embedCode.match(/data-instgrm-permalink="([^"]+)"/);
  return match ? match[1] : `${embed.name || "post"}-${index}`;
};

const MeetRecaps = () => {
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  // Which captions the reader has expanded, and which actually overflow the
  // collapsed clamp (so the "Read more" button only appears when there's more).
  const [expanded, setExpanded] = useState({});
  const [overflowing, setOverflowing] = useState({});
  const captionRefs = useRef({});
  const topRef = useRef(null);

  // --- One-time load of Instagram's embed.js -----------------------
  useEffect(() => {
    const src = "https://www.instagram.com/embed.js";
    if (!document.querySelector(`script[src="${src}"]`)) {
      const script = document.createElement("script");
      script.async = true;
      script.defer = true;
      script.src = src;
      document.body.appendChild(script);
      script.onload = () => window.instgrm?.Embeds?.process();
    } else {
      // if already present (from another route), process immediately
      window.instgrm?.Embeds?.process();
    }
  }, []);
  // -----------------------------------------------------------------

  // Filter by athlete name. An active, non-matching search yields an empty list
  // (and an empty state) rather than silently falling back to every recap.
  const list = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return instaEmbeds;
    return instaEmbeds.filter((embed) =>
      (embed.name || "").toLowerCase().includes(term)
    );
  }, [query]);

  const totalPages = Math.max(1, Math.ceil(list.length / EMBEDS_PER_PAGE));

  const indexOfLastEmbed = currentPage * EMBEDS_PER_PAGE;
  const indexOfFirstEmbed = indexOfLastEmbed - EMBEDS_PER_PAGE;
  const currentEmbeds = useMemo(
    () => list.slice(indexOfFirstEmbed, indexOfLastEmbed),
    [list, indexOfFirstEmbed, indexOfLastEmbed]
  );

  // Re-process embeds whenever the visible set changes (page change, search).
  // Without this, blockquotes injected after the initial scan stay raw.
  useEffect(() => {
    window.instgrm?.Embeds?.process();
  }, [currentEmbeds]);

  // After the visible set renders (collapsed), flag which captions are clipped
  // by the clamp so only those get a "Read more" toggle.
  useLayoutEffect(() => {
    const next = {};
    Object.entries(captionRefs.current).forEach(([key, el]) => {
      if (el) next[key] = el.scrollHeight > el.clientHeight + 2;
    });
    setOverflowing(next);
  }, [currentEmbeds]);

  const toggleExpand = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSearch = (e) => {
    setQuery(e.target.value);
    setCurrentPage(1);
    setExpanded({});
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    setExpanded({});
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Page buttons with first/last anchors + ellipses for long ranges.
  const generatePaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);

    const pageButton = (i) => (
      <button
        key={i}
        type="button"
        onClick={() => paginate(i)}
        className={`recaps-pagination__page${
          currentPage === i ? " is-active" : ""
        }`}
        aria-label={`Page ${i}`}
        aria-current={currentPage === i ? "page" : undefined}
      >
        {i}
      </button>
    );

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) buttons.push(pageButton(i));
      return buttons;
    }

    buttons.push(pageButton(1));

    let startPage = Math.max(2, currentPage - halfVisible);
    let endPage = Math.min(totalPages - 1, currentPage + halfVisible);
    if (currentPage <= halfVisible + 1) {
      endPage = Math.min(totalPages - 1, maxVisiblePages - 1);
    }
    if (currentPage >= totalPages - halfVisible) {
      startPage = Math.max(2, totalPages - maxVisiblePages + 2);
    }

    if (startPage > 2) {
      buttons.push(
        <span key="ellipsis1" className="recaps-pagination__ellipsis">
          &hellip;
        </span>
      );
    }
    for (let i = startPage; i <= endPage; i++) buttons.push(pageButton(i));
    if (endPage < totalPages - 1) {
      buttons.push(
        <span key="ellipsis2" className="recaps-pagination__ellipsis">
          &hellip;
        </span>
      );
    }
    buttons.push(pageButton(totalPages));

    return buttons;
  };

  const renderPagination = (position) => {
    if (totalPages <= 1) return null;
    return (
      <nav
        className={`recaps-pagination recaps-pagination--${position}`}
        aria-label="Recaps pagination"
      >
        <button
          type="button"
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
          className="recaps-pagination__nav"
          aria-label="Previous page"
        >
          <FaChevronLeft aria-hidden="true" />
          <span>Prev</span>
        </button>
        {generatePaginationButtons()}
        <button
          type="button"
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="recaps-pagination__nav"
          aria-label="Next page"
        >
          <span>Next</span>
          <FaChevronRight aria-hidden="true" />
        </button>
      </nav>
    );
  };

  return (
    <div className="meet-recaps" ref={topRef}>
      <header
        className="recaps-hero"
        style={{ backgroundImage: `url(${recapsHeader})` }}
      >
        <div className="recaps-hero__scrim" />
        <div className="recaps-hero__inner">
          <p className="recaps-hero__eyebrow">On the platform</p>
          <h1 className="recaps-hero__title">Meet Recaps</h1>
          <p className="recaps-hero__subtitle">
            Competition results and the story behind each prep, straight from the
            coach who programmed it.
          </p>
        </div>
      </header>

      <section className="recaps-section">
        <div className="recaps-controls">
          <div className="recaps-search">
            <FaSearch className="recaps-search__icon" aria-hidden="true" />
            <input
              id="recaps-search"
              type="search"
              value={query}
              placeholder="Search by athlete name"
              onChange={handleSearch}
              className="recaps-search__input"
              aria-label="Search recaps by athlete name"
            />
          </div>
          <p className="recaps-count" aria-live="polite">
            {list.length} {list.length === 1 ? "recap" : "recaps"}
          </p>
        </div>

        {list.length === 0 ? (
          <div className="recaps-empty">
            <p className="recaps-empty__title">No recaps found</p>
            <p className="recaps-empty__body">
              No athlete matches &ldquo;{query.trim()}&rdquo;. Try another name.
            </p>
          </div>
        ) : (
          <>
            {renderPagination("top")}

            <div className="recaps-grid">
              {currentEmbeds.map((embed, index) => {
                const cardKey = keyFor(embed, index);
                const isExpanded = !!expanded[cardKey];
                return (
                  <article className="recap-card" key={cardKey}>
                    <div className="recap-card__embed">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: stripScripts(embed.embedCode),
                        }}
                      />
                    </div>
                    <div className="recap-card__body">
                      {embed.name && (
                        <h2 className="recap-card__name">{embed.name}</h2>
                      )}
                      <p
                        className={`recap-card__caption${
                          isExpanded ? " is-expanded" : ""
                        }`}
                        ref={(el) => {
                          if (el) captionRefs.current[cardKey] = el;
                          else delete captionRefs.current[cardKey];
                        }}
                      >
                        {embed.caption}
                      </p>
                      {overflowing[cardKey] && (
                        <button
                          type="button"
                          className="recap-card__readmore"
                          aria-expanded={isExpanded}
                          onClick={() => toggleExpand(cardKey)}
                        >
                          {isExpanded ? "Read less" : "Read more"}
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>

            {renderPagination("bottom")}
          </>
        )}
      </section>
    </div>
  );
};

export default MeetRecaps;
